'use client';

import { useState } from 'react';
import { ChevronDown, HelpCircle, MessageCircle } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Icon } from '@/components/ui/Icon';
import { faqs } from '@/content/faqs';
import { cn } from '@/lib/utils';
import { WHATSAPP_URL } from '@/lib/whatsapp';

export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="preguntas" data-section="faq" className="bg-bg-primary py-20 sm:py-24">
      <div className="container mx-auto px-6">
        <div className="grid gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:items-start">
          <div className="lg:sticky lg:top-24">
            <Badge variant="outline" className="mb-5">
              Antes de mover tu agenda
            </Badge>
            <h2 className="font-heading text-3xl font-bold leading-tight text-text-primary sm:text-5xl">
              Algunas Preguntas Frecuentes
            </h2>
            <p className="mt-5 text-base leading-8 text-text-secondary">
              Si alguna respuesta depende de tu número, tu equipo o tu forma de trabajar, lo vemos
              contigo por WhatsApp. La idea es configurar bien, no venderte afán.
            </p>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-track-event="whatsapp_cta_click"
              data-cta-location="faq"
              data-cta-text="Preguntar por WhatsApp"
              className="mt-8 inline-flex items-center gap-2 rounded-button border border-accent/35 bg-accent/10 px-5 py-3 font-heading text-sm font-semibold text-accent transition-colors hover:bg-accent hover:text-bg-primary"
            >
              <Icon icon={MessageCircle} size={18} />
              Preguntar por WhatsApp
            </a>
          </div>

          <div className="overflow-hidden rounded-mockup border border-border bg-bg-primary/70">
            {faqs.map((faq, index) => {
              const isOpen = openIndex === index;
              const panelId = `faq-panel-${index}`;

              return (
                <div key={faq.question} className="border-b border-border last:border-b-0">
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    data-faq-question={faq.question}
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="grid w-full grid-cols-[auto_1fr_auto] items-center gap-4 px-5 py-5 text-left transition-colors hover:bg-bg-secondary/70"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-button bg-accent/10 text-accent">
                      <Icon icon={HelpCircle} size={18} />
                    </span>
                    <span className="font-heading text-base font-semibold leading-6 text-text-primary sm:text-lg">
                      {faq.question}
                    </span>
                    <Icon
                      icon={ChevronDown}
                      size={20}
                      className={cn('text-accent transition-transform', isOpen && 'rotate-180')}
                    />
                  </button>
                  <div id={panelId} hidden={!isOpen} className="px-5 pb-5 pl-[4.75rem]">
                    <p className="max-w-3xl text-sm leading-7 text-text-secondary sm:text-base">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
